import { useRef, useState, useEffect } from 'react';
import { Button, Modal, Spin, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { smilepassportApi } from '../../../api/smilepassport.api';
import { usePermission } from '../../../hooks/usePermission';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import dayjs from 'dayjs';
import { resolveFileUrl } from '../../../utils/helpers';

const PAGE_COUNT = 12;
const PAGE_WIDTH = 496;
const PAGE_HEIGHT = 709;
const PDF_WIDTH_MM = 148;
const PDF_HEIGHT_MM = 210;

const pageImages = Array.from(
  { length: PAGE_COUNT },
  (_, index) => `/smilepassport-pages/page-${String(index + 1).padStart(2, '0')}.png`,
);

const toDisplayValue = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  return value;
};

const computeAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age;
};

const clinicLocations = [
  {
    name: "We Little Hyderabad",
    details: [
      "2nd floor, Check Post Metro station north entrance,",
      "Opposite 10 TV, Jawahar Colony, Jubilee Hills,",
      "Hyderabad - 500033, Telangana, India.",
      "M: +91 40 4951 9459 | E: infohyderabad@wetittle.org",
    ],
  },
  {
    name: "We Little Bangalore",
    details: [
      "No.557, 1st Floor, 9th A Main Rd, 1st Stage Rd,",
      "Binnamangala, Hoysala Nagar, Indiranagar,",
      "Bengaluru - 560038, Karnataka, India.",
      "M: +91 96068 97510 | E: infobengaluru@wetittle.org",
    ],
  },
  {
    name: "We Little Chennai",
    details: [
      "27, Dr Radha Krishnan Salai, Dwarka Colony,",
      "Mylapore, Chennai - 600004, Tamilnadu, India.",
      "M: +91 44-43535566 | +91 97515 58000",
      "E: infochennai@welittle.org",
    ],
  },
  {
    name: "We Little Coimbatore",
    details: [
      "739, Avinashi Road, 3rd Floor, Sri Ram Towers, Near",
      "Anna Statue, Coimbatore - 641018, Tamilnadu, India.",
      "M: +91 97869 29892 | 422-4500450 | 422-2555450",
      "E: info@wetittle.org",
    ],
  },
  {
    name: "Little We Kochi",
    details: [
      "3rd Floor, CHERUKARA COMPLEX, near Overbridge,",
      "Vyttila, Kochi, Ernakulam, Kerala - 682019,",
      "Tamilnadu, India.",
      "M: +91 79942 48166 | E: info@littlewe.org",
    ],
  },
  {
    name: "Little We Karur",
    details: [
      "No1, Eidka Pallivaasal complex,",
      "Above Sumathi sweets First floor, Covai main road,",
      "Karur 639002, Tamilnadu, India.",
      "M: +91 84389 82718 | E: info@littlewe.org",
    ],
  },
  {
    name: "Little We Trichy",
    details: [
      "C, 31, 11th Cross W, West Thillai Nagar, Tennur,",
      "Tiruchirappalli - 620018, Tamil Nadu, India.",
      "M: +91 84389 93718 | E: info@littlewe.org",
    ],
  },
];

const passportStyles = `
  .smile-passport-shell {
    background: #9e9e9e;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    padding: 24px;
    font-family: Arial, Helvetica, sans-serif;
  }

  .smile-passport-page {
    width: ${PAGE_WIDTH}px;
    height: ${PAGE_HEIGHT}px;
    background: #ffffff;
    box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.45);
    overflow: hidden;
    page-break-after: always;
    position: relative;
  }

  .smile-page-bg {
    display: block;
    height: 100%;
    inset: 0;
    object-fit: cover;
    position: absolute;
    width: 100%;
  }

  .smile-overlay {
    color: #173f59;
    font-weight: 700;
    line-height: 1.25;
    position: absolute;
    z-index: 1;
  }

  .smile-gold {
    color: #b47a17;
  }

  .smile-white {
    color: #ffffff;
  }

  .smile-body-copy {
    color: #58595b;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.45;
    text-align: left;
  }

  .smile-title {
    color: #b47a17;
    font-size: 22px;
    font-weight: 700;
  }

  .smile-field {
    color: #2e3092;
    font-size: 18px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .smile-small-field {
    color: #173f59;
    font-size: 13px;
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .smile-check-label {
    color: #58595b;
    font-size: 22px;
    font-weight: 500;
  }

  .smile-check-value {
    color: #173f59;
    font-size: 17px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .smile-form-line {
    color: #58595b;
    font-size: 19px;
    font-weight: 800;
  }

  .smile-form-value {
    color: #173f59;
    font-size: 15px;
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

 .smile-vertical-column {
  display: flex;
  flex-direction: column;
  align-items: center;
}

 .smile-vertical-line {
  border-left: 2px dashed #58595b;
  height: 430px;
  width: 0;
}

  .smile-vertical-label,
  .smile-vertical-value {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    font-size: 14px;
    text-align: center;
    width: 100px;
    max-width: 100px;
    white-space: normal;
    line-height: 5.2;
    word-break: break-word;
  }

 .smile-vertical-label {
  order: 1;
}
.smile-vertical-value {
  order: 2;
  margin-top: -280px; /* increase as needed */
}

  .smile-habit-box {
    background-color: #ffffff;
    background-image: repeating-linear-gradient(to bottom, transparent, transparent 27px, #d8e3ec 28px);
    background-position: 0 14px;
    background-size: 100% 28px;
    border: 2px solid #2c6898;
    border-radius: 12px;
    box-sizing: border-box;
    overflow: hidden;
    padding: 14px;
    position: absolute;
  }

  .smile-habit-text {
    color: #2c6898;
    font-size: 16px;
    font-weight: 500;
    line-height: 28px;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .smile-habit-footer {
    align-items: center;
    background-color: #1d4e6a;
    border-radius: 8px;
    display: flex;
    height: 50px;
    justify-content: center;
    position: absolute;
  }

  .smile-form-lines {
  position: absolute;
  inset: 0;
}
 
.smile-form-line {
  position: absolute;
  bottom: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
 
.smile-form-line span {
  height: 520px;
  border-left: 2px dashed #606064;
}
 
.smile-form-line .label,
.smile-form-line span {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: 16px;
  color: #606064;
  margin-top: 10px;
}
 
.smile-form-line .value {
  color: #173f59;
  font-size: 14px;
  max-height: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
}
 
.custom-form-lines .smile-form-line {
    font-weight: 500;
}

.smile-term-title {
  color: #1d4e6a;
  font-size: 14px;
  font-weight: 800;
  text-transform: uppercase;
}

.smile-term-label {
  color: #58595b;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.smile-term-value {
  color: #173f59;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  max-height: 48px;
  overflow: hidden;
  word-break: break-word;
}
`;

const Page = ({ number, children }) => (
  <div className="smile-passport-page">
    {number !== 10 && (
      <img
        alt=""
        className="smile-page-bg"
        src={pageImages[number - 1]}
      />
    )}
    {children}
  </div>
);

const Overlay = ({ children, className = '', style }) => (
  <div className={`smile-overlay ${className}`} style={style}>
    {children}
  </div>
);

const IntroPage = () => (
  <Page number={2}>
    <Overlay className="smile-body-copy" style={{ left: 44, top: 95, width: 390 }}>
      We Little is India’s first and only preventive pediatric dental clinic 
dedicated to holistic health. We focus on healthy habits from an 
early age, encouraging proper nasal breathing, restful sleep, 
cavity prevention and care, and myofunctional therapy that 
naturally supports teeth alignment. Our Smile Passport 
encourages a 3-monthly check-up routine that assesses your 
child’s airway, sleep, and dental alignment. Early detection of 
these issues is important because it helps your child grow 
healthier, breathe better, sleep well, and smile with confidence — 
all naturally and gently
    </Overlay>
  </Page>
);

const IssuePages = () => (
  <>
    <Page number={4}>
      <Overlay className="smile-body-copy" style={{ left: 44, top: 130, width: 410 }}>
        Your teeth are like friends standing in a line. If some teeth don’t stand straight, they may bump into each other or leave gaps. This is called an alignment issue. When teeth are not in the right place, it can make chewing harder and sometimes even change the way your smile looks. Dentists help teeth find their “perfect place” so they can stand neatly in line.
      </Overlay>
    </Page>

    <Page number={5}>
      <Overlay className="smile-body-copy" style={{ left: 44, top: 120, width: 410 }}>
        When we sleep, our mouth and teeth also need to rest. But if your 
teeth and jaws are not in the right position, it can make it harder 
to breathe or rest well. Sometimes kids may snore, toss and turn, 
or wake up feeling tired. Dentists look at your teeth and jaws to 
make sure they are helping you sleep peacefully like a superhero 
charging up at night.

      </Overlay>
    </Page>

    <Page number={6}>
      <Overlay className="smile-body-copy" style={{ left: 44, top: 120, width: 410 }}>
        Breathing through your nose is like giving your body fresh, clean 
air. But if your teeth, jaws, or tongue are not in the right place, you 
might start breathing through your mouth instead. Mouth 
breathing can make your teeth grow in a funny way, make your 
mouth dry, and even affect your smile. That’s why dentists check 
if your teeth and jaws are helping you breathe the right way.
      </Overlay>
    </Page>
  </>
);

const TERM_CONTENT_LEFT = 92;
const TERM_CONTENT_WIDTH = 384;
const TERM_TITLE_OFFSET = 24;
const TERM_FIELD_OFFSETS = [68, 148, 228];
const TERM_HALF_OFFSET = 340;

const TermBlock = ({ term, baseTop }) => {
  if (!term) return null;

  const title = `TERM ${toDisplayValue(term.term_no)} (${toDisplayValue(term.startMonth)} - ${toDisplayValue(term.endMonth)})`;
  const fields = [
    { label: 'AIRWAY:', value: toDisplayValue(term.airway) },
    { label: 'SLEEP:', value: toDisplayValue(term.sleep_status) },
    { label: 'ALIGNMENT:', value: toDisplayValue(term.alignment) },
  ];

  return (
    <>
      <Overlay className="smile-term-title" style={{ left: TERM_CONTENT_LEFT, top: baseTop + TERM_TITLE_OFFSET, width: TERM_CONTENT_WIDTH }}>
        {title}
      </Overlay>
      {fields.map((field, index) => (
        <Overlay key={field.label} style={{ left: TERM_CONTENT_LEFT, top: baseTop + TERM_FIELD_OFFSETS[index], width: TERM_CONTENT_WIDTH }}>
          <div className="smile-term-label">{field.label}</div>
          <div className="smile-term-value">{field.value}</div>
        </Overlay>
      ))}
    </>
  );
};

const TermsCheckupPage = ({ number, terms }) => (
  <Page number={number}>
    <TermBlock term={terms[0]} baseTop={0} />
    <TermBlock term={terms[1]} baseTop={TERM_HALF_OFFSET} />
  </Page>
);

export const SmilepassportPreviewContent = ({ record, child, previewRef }) => {
  const childData = record.child || child || {};
  const parentData = childData.parent || {};

  const childName = toDisplayValue(childData.name);
  const childImage = childData.childImage
    ? resolveFileUrl(childData.childImage)
    : null;
  const childAge = childData.age ?? computeAge(childData.dateOfBirth) ?? '-';
  const childHobbies = toDisplayValue(childData.hobbies);
  const parentPhone = toDisplayValue(parentData.phone);
  const parentAddress = toDisplayValue(childData.address ?? parentData.address);
  const academicYear = toDisplayValue(record.academic_year);
  const concern = toDisplayValue(record.concern);
  const childFood = toDisplayValue(childData.favouriteFood);
  const nextCheckup = record.next_checkup ? dayjs(record.next_checkup).format('DD/MM/YYYY') : '-';
  const toothBrushingNotes = toDisplayValue(record.tooth_brushing_completed);
  const nasalBreathingNotes = toDisplayValue(record.nasal_breathing_completed);
  const terms = Array.isArray(record.terms) ? record.terms : [];

  const schoolFromEvent = record.event?.school;
  const schoolFromChild = childData.school;
  const schoolData = (typeof schoolFromEvent === 'object' && schoolFromEvent) || (typeof schoolFromChild === 'object' && schoolFromChild) || {};
  const schoolLogoUrl = schoolData.logo ? resolveFileUrl(schoolData.logo) : null;
  const schoolName = schoolData.name || '';

  return (
    <div>
      <style>{passportStyles}</style>
      <div className="smile-passport-shell" ref={previewRef}>
        <Page number={1}>
          {(schoolLogoUrl || schoolName) && (
            <Overlay style={{ left: 148, top: 310, width: 200, textAlign: 'center' }}>
              {schoolLogoUrl && (
                <img
                  alt=""
                  src={schoolLogoUrl}
                  style={{
                    width: 70,
                    height: 70,
                    objectFit: 'contain',
                    display: 'block',
                    margin: '0 auto',
                  }}
                />
              )}
              {schoolName && (
                <div style={{
                  marginTop: 2,
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#ffffff',
                  textAlign: 'center',
                  lineHeight: 1.2,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  maxWidth: 200,
                  margin: '2px auto 0',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}>
                  {schoolName}
                </div>
              )}
            </Overlay>
          )}
          <Overlay className="smile-small-field" style={{ left: 145, textAlign: 'center', top: 278, width: 210, color: '#000' }}>
            <span style={{ color: '#000' }}>Academic Year:</span>{' '}
            <span style={{ color: '#2c6898' }}>{academicYear}</span>
          </Overlay>
          <Overlay className="smile-small-fieldvalue" style={{ left: 100, top: 600, width: 301, fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 12, fontWeight: 397, color: '#f8f2f2', textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.8, textAlign: 'center' }}>
            <span style={{ color: '#f5eeee' }}>BANGALORE | CHENNAI | COIMBATORE |
              HYDERABAD | KARUR | KOCHI | TRICHY
              </span>{' '}
          </Overlay>
        </Page>

        <IntroPage />

        <Page number={3}>
          {/* Title */}
          <Overlay style={{ color: '#1d4e6a', fontSize: 28, fontWeight: 900, left: 35, top: 25 }}>
            All About Me
          </Overlay>

          {/* MY NAME label */}
          <Overlay style={{ color: '#2e3092', fontSize: 13,  left: 54, letterSpacing: 1, textAlign: 'center', top: 88, width: 205 }}>
            MY NAME
          </Overlay>

          {/* Name value */}
          <Overlay className="smile-field" style={{ left: 54, textAlign: 'center', top: 112, width: 205 }}>
            {childName}
          </Overlay>

          {/* I AM label */}
          <Overlay style={{ color: '#2e3092', fontSize: 13, fontWeight: 600, left: 140, letterSpacing: 1, top: 170 }}>
            I AM
          </Overlay>

          {/* Age value */}
          <Overlay className="smile-field" style={{ left: 134, textAlign: 'center', top: 190, width: 45 }}>
            {childAge}
          </Overlay>

          {/* YEARS OLD label */}
          <Overlay style={{ color: '#2e3092', fontSize: 13, fontWeight: 600, left: 92, letterSpacing: 1, textAlign: 'center', top: 280, width: 130 }}>
            YEARS OLD
          </Overlay>

          {/* Child photo inside the polaroid frame */}
          {childImage && (
            <img
              alt=""
              src={childImage}
              style={{
                left: 296,
                top: 108,
                width: 131,
                height: 134,
                objectFit: 'cover',
                position: 'absolute',
                zIndex: 0,
              }}
            />
          )}

          {/* THIS IS ME label */}
          <Overlay style={{ background: '#2e3092', color: '#ffffff', fontSize: 13, fontWeight: 800, left: 288, letterSpacing: 1, padding: '3px 0', textAlign: 'center', top: 249, width: 150 }}>
            THIS IS ME
          </Overlay>

          {/* MY FAVORITE FOOD label */}
          <Overlay style={{ color: '#2e3092', fontSize: 13, fontWeight: 800, left: 62, letterSpacing: 1, top: 310 }}>
            MY FAVORITE FOOD
          </Overlay>

          {/* Concern/food value */}
          <Overlay className="smile-field" style={{ left: 52, textAlign: 'center', top: 325, width: 372 }}>
            {childFood}
          </Overlay>

          {/* I LOVE TO label */}
          <Overlay style={{ color: '#2e3092', fontSize: 13, fontWeight: 600, left: 108, letterSpacing: 1, top: 490 }}>
            I LOVE TO
          </Overlay>

          {/* Hobbies value */}
          <Overlay className="smile-field" style={{fontSize: '20px', left: 70, textAlign: 'center', top: 550, width: 135 }}>
            {childHobbies}
          </Overlay>
        </Page>

        <IssuePages />

        <Page number={7} />

        <TermsCheckupPage number={8} terms={[terms[0], terms[1]]} />
        <TermsCheckupPage number={9} terms={[terms[2], terms[3]]} />

        <Page number={10}>
          <Overlay className="smile-title" style={{ left: 40, top: 28, width: 420 }}>
            My Healthy Smile Habits
          </Overlay>
          <Overlay className="smile-body-copy" style={{ fontSize: 12, left: 40, top: 60, width: 416 }}>
          </Overlay>

          <Overlay className="smile-check-value" style={{ left: 40, top: 92, textAlign: 'center', width: 416 }}>
            Tooth Brushing
          </Overlay>
          <div className="smile-habit-box" style={{ height: 215, left: 40, top: 116, width: 416 }}>
            <div className="smile-habit-text">{toothBrushingNotes}</div>
          </div>

          <Overlay className="smile-check-value" style={{ left: 40, top: 345, textAlign: 'center', width: 416 }}>
            Nasal Breathing
          </Overlay>
          <div className="smile-habit-box" style={{ height: 215, left: 40, top: 369, width: 416 }}>
            <div className="smile-habit-text">{nasalBreathingNotes}</div>
          </div>

          <div className="smile-habit-footer" style={{ left: 40, right: 40, top: 615 }}>
            <span className="smile-white" style={{ fontSize: 20, fontWeight: 900 }}>GET MY FREE GIFT</span>
          </div>
        </Page>

        <Page number={11}>
          <div
            style={{
              bottom: 200,
              display: 'flex',
              flexDirection: 'column',
              fontFamily: 'Arial, Helvetica, sans-serif',
              justifyContent: 'space-between',
              left: 40,
              position: 'absolute',
              right: 40,
              top: 200,
              zIndex: 1,
            }}
          >
            {[
              { label: 'Name',            value: childName     },
              { label: 'Address',         value: parentAddress },
              { label: 'Concern',         value: concern       },
              { label: 'Favourite Hobby', value: childHobbies  },
              { label: 'Parents Number',  value: parentPhone   },
              { label: 'Next Checkup',    value: nextCheckup   },
            ].map((field, index) => (
              <div
                key={`form-field-${index}`}
                style={{
                  alignItems: 'flex-end',
                  borderBottom: '1.5px dashed #606064',
                  display: 'flex',
                  gap: 6,
                  overflow: 'hidden',
                  paddingBottom: 5,
                  paddingTop: 5,
                }}
              >
                {field.label && (
                  <span
                    style={{
                      color: '#606064',
                      flexShrink: 0,
                      fontSize: 13,
                      fontWeight: 500,
                      lineHeight: 1.3,
                    }}
                  >
                    {field.label}:
                  </span>
                )}
                <span
                  style={{
                    color: '#173f59',
                    flex: 1,
                    fontSize: 13,
                    fontWeight: 700,
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {field.value}
                </span>
              </div>
            ))}
          </div>
        </Page>

        <Page number={12}>
          <Overlay className="smile-small-fieldvalue" style={{ left: 100, top: 127, width: 303, fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 13, fontWeight: 397, color: '#f8f2f2', textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.8, textAlign: 'center' }}>
            <span style={{ color: '#f5eeee' }}>SCAN TO KNOW MORE
              </span>{' '}
          </Overlay>
            {clinicLocations.map((location, index) => (
              <Overlay
                className="smile-white"
                key={location}
                style={{
                  fontSize: 8,
                  fontWeight: 500,
                  left: index % 2 === 0 ? 42 : 256,
                  top: 330 + Math.floor(index / 2) * 92,
                  width: 218,
                }}
              >
                <div style={{ fontWeight: 500, marginBottom: 4 }}>
                  {location.name}
                </div>
 
                {location.details.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </Overlay>
            ))}
          </Page>
      </div>
    </div>
  );
};

const SmilepassportPreviewModal = ({ open, onClose, record, child }) => {
  const previewRef = useRef(null);
  const [pdfStatus, setPdfStatus] = useState('checking');
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const { isAdmin } = usePermission();

  const pdfUrl = record?.pdf_url ?? null;

  useEffect(() => {
    if (!open) {
      setIframeLoaded(false);
      return;
    }
    if (!pdfUrl) { setPdfStatus('missing'); return; }
    setPdfStatus('checking');
    setIframeLoaded(false);
    fetch(pdfUrl, { method: 'HEAD' })
      .then((res) => setPdfStatus(res.ok ? 'ready' : 'missing'))
      .catch(() => setPdfStatus('missing'));
  }, [open, pdfUrl]);

  if (!record) return null;

  const handleDownloadPdf = () => {
    if (!pdfUrl || pdfStatus !== 'ready') return;
    const childData = record.child || child || {};
    const childName = String(toDisplayValue(childData.name)).replace(/\s+/g, '_');
    const academicYear = String(toDisplayValue(record.academic_year)).replace(/\s+/g, '_');
    const fileName = `${childName || 'smile-passport'}-${academicYear}.pdf`;

    const anchor = document.createElement('a');
    anchor.href = pdfUrl;
    anchor.setAttribute('download', fileName);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  return (
    <Modal
      title="Smile Passport Preview"
      open={open}
      onCancel={onClose}
      width={680}
      styles={{ body: { padding: 0 } }}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {isAdmin && pdfStatus === 'ready' && (
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadPdf}>
              Download PDF
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </div>
      }
      destroyOnClose
    >
      {pdfStatus === 'ready' ? (
        <div style={{ position: 'relative', height: '80vh' }}>
          {!iframeLoaded && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              background: '#fff', zIndex: 1,
            }}>
              <Spin size="large" tip="Loading PDF..." />
            </div>
          )}
          <iframe
            src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            title="Smile Passport PDF"
            onLoad={() => setIframeLoaded(true)}
            style={{ border: 'none', display: 'block', height: '100%', width: '100%' }}
          />
        </div>
      ) : pdfStatus === 'checking' ? (
        <div style={{ alignItems: 'center', color: '#888', display: 'flex', flexDirection: 'column', gap: 12, justifyContent: 'center', minHeight: 300 }}>
          <Spin size="large" tip="Loading PDF..." />
        </div>
      ) : (
        <div style={{ maxHeight: '80vh', overflow: 'auto' }}>
          <SmilepassportPreviewContent record={record} child={child} previewRef={previewRef} />
        </div>
      )}
    </Modal>
  );
};

export default SmilepassportPreviewModal;
